import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X } from "lucide-react";
import { toast } from "sonner";
import { readPreference, savePreference, removeLegacyPreferences } from "@/lib/tabPreferences";

const DISMISS_KEY = "kandula-install-dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

let deferredPrompt = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; });

export const requestInstall = async () => {
  if (isStandalone()) { toast.success("KHF-APP is already installed on this device"); return; }
  if (deferredPrompt) {
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    return;
  }
  toast(isIos() ? "In Safari, tap Share → Add to Home Screen" : "Open your browser menu and choose Install app / Add to Home Screen", { duration: 6000 });
};

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    removeLegacyPreferences();
    if (isStandalone() || readPreference(DISMISS_KEY)) return;
    const onPrompt = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setDeferred(e);
      setVisible(true);
    };
    if (deferredPrompt) { setDeferred(deferredPrompt); setVisible(true); }
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIos()) {
      setIos(true);
      const t = setTimeout(() => setVisible(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    savePreference(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-2xl border border-cream-muted bg-ragi-dark p-4 text-cream shadow-2xl sm:inset-x-auto sm:left-6"
          role="dialog"
          aria-label="Install KHF-APP"
          data-testid="install-prompt"
        >
          <button onClick={dismiss} aria-label="Dismiss" className="absolute right-3 top-3 rounded-full p-1 text-cream/60 transition-colors hover:text-cream" data-testid="install-prompt-dismiss">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <img src="/icons/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
            <div className="flex-1 pr-5">
              <p className="font-display text-lg font-semibold leading-tight" data-testid="install-app-name">Get KHF-APP</p>
              {ios ? (
                <p className="mt-1 text-xs leading-relaxed text-cream/75" data-testid="install-prompt-ios-hint">
                  Tap <Share className="inline h-3.5 w-3.5 align-text-bottom" aria-label="Share" /> in Safari, then <span className="font-semibold text-cream">Add to Home Screen</span>.
                </p>
              ) : (
                <p className="mt-1 text-xs leading-relaxed text-cream/75">Order faster — add it to your home screen. No Play Store needed.</p>
              )}
              {!ios && (
                <button onClick={install} className="mt-3 inline-flex items-center gap-2 rounded-full bg-saffron px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-ragi-dark transition-transform hover:scale-[1.03]" data-testid="install-prompt-install-button">
                  <Download className="h-3.5 w-3.5" /> Install
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
